import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { 
  getFacilityGroups, 
  createFacilityGroup, 
  updateFacilityGroup, 
  deleteFacilityGroup 
} from '../../services/facilityGroupService';
import { getAllFacilities } from '../../services/facilityService';
//import { useSelector } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useLocale } from '../../context/LocaleContext';
import { styled } from '@mui/material/styles';
import { UserRole } from '../../types/enums';
import { useUserData } from '../../hooks/useUserData';
import { useSelector } from 'react-redux';

const HeaderCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const FacilityGroups = () => {
  const navigate = useNavigate();
  const { user } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    locale: 'en',
    facilities: []
  });
  const currentUser = user;
  const userRoles = user?.roles || [user?.role].filter(Boolean);
  const { t } = useLocale();
  const [searchTerm, setSearchTerm] = useState('');

  const localeOptions = [
    { value: 'en', label: 'English' },
    { value: 'ar', label: 'العربية (Arabic)' }
  ];

  const getFacilityAssignment = (facilityId) => {
    return groups.find(g => 
      g.id !== selectedGroup?.id && 
      Array.isArray(g.facilities) && 
      g.facilities.includes(facilityId)
    );
  };

  const getFacilitiesForGroup = (groupId) => {
    if (groupId === 'ungrouped') {
      return facilities.filter(facility => 
        !groups.some(group => group.facilities?.includes(facility.id))
      );
    }
    const group = groups.find(g => g.id === groupId);
    if (!group) return [];
    return facilities.filter(facility => 
      group.facilities?.includes(facility.id)
    );
  };

  const handleToggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const fetchGroups = useCallback(async () => {
    try {
      // Use service layer to fetch facility groups
      const groupsData = await getFacilityGroups();
      setGroups(groupsData);

      // Expand all groups by default for better UX
      const expanded = {};
      groupsData.forEach(group => {
        expanded[group.id] = true;
      });
      expanded['ungrouped'] = true;
      setExpandedGroups(expanded);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError(t('failedToLoadGroups'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchFacilities = useCallback(async () => {
    try {
      let facilitiesData = [];

      if (userRoles.includes(UserRole.ADMIN)) {
        // Admin can see all facilities using service layer
        facilitiesData = await getAllFacilities();
      } else if (userRoles.includes(UserRole.FACILITY_ADMIN)) {
        // Facility admin can only see facilities they are assigned to
        
        if (userFacilities && userFacilities.length > 0) {
          const assignedFacilityIds = userFacilities.map(uf => uf.facilityId);
          
          // Fetch only assigned facilities using service layer
          const allFacilities = await getAllFacilities();
          facilitiesData = allFacilities.filter(facility => 
            assignedFacilityIds.includes(facility.id)
          );
        }
      }

      setFacilities(facilitiesData);
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError(t('failedToLoadFacilities'));
    }
  }, [userRoles, t, userFacilities]);

  useEffect(() => {
    fetchGroups();
    fetchFacilities();
  }, [fetchGroups, fetchFacilities]);

  const handleOpenDialog = (group = null) => {
    if (group) {
      setSelectedGroup(group);
      setFormData({
        name: group.name,
        address: group.address || '',
        phone: group.phone || '',
        fax: group.fax || '',
        email: group.email || '',
        locale: group.locale || 'en',
        facilities: Array.isArray(group.facilities) ? group.facilities : []
      });
    } else {
      setSelectedGroup(null);
      setFormData({
        name: '',
        address: '',
        phone: '',
        fax: '',
        email: '',
        locale: 'en',
        facilities: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedGroup(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      fax: '',
      email: '',
      locale: 'en',
      facilities: []
    });
  };

  const handleSave = async () => {
    try {
      console.log('Saving group with facilities:', formData.facilities);
      
      if (!formData.name) {
        setError(t('groupNameRequired'));
        return;
      }

      const groupData = {
        name: formData.name,
        address: formData.address || '',
        phone: formData.phone || '',
        fax: formData.fax || '',
        email: formData.email || '',
        locale: formData.locale || 'en',
        facilities: Array.isArray(formData.facilities) ? formData.facilities : [],
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      };

      if (!selectedGroup) {
        groupData.createdAt = new Date().toISOString();
        groupData.createdBy = currentUser.uid;
      }

      if (selectedGroup) {
        // Update existing group using service layer
        await updateFacilityGroup(selectedGroup.id, groupData);
      } else {
        // Create new group using service layer
        await createFacilityGroup(groupData);
      }

      await fetchGroups();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving group:', err);
      setError(t('failedToSaveGroup'));
    }
  };

  const handleDelete = async (groupId) => {
    if (window.confirm(t('confirmDeleteGroup'))) {
      try {
        // Delete group using service layer
        await deleteFacilityGroup(groupId);
        fetchGroups();
      } catch (err) {
        console.error('Error deleting group:', err);
        setError(t('failedToDeleteGroup'));
      }
    }
  };

  const toggleFacility = (facilityId) => {
    console.log('Current facilities:', formData.facilities);
    console.log('Toggling facility:', facilityId);
    setFormData(prev => {
      const facilities = prev.facilities.includes(facilityId)
        ? prev.facilities.filter(id => id !== facilityId)
        : [...prev.facilities, facilityId];
      console.log('Updated facilities:', facilities);
      return { ...prev, facilities };
    });
  };

  const handleAddFacility = (groupId) => {
    // Navigate to facility creation page with groupId as query param
    navigate(`/admin/facilities/new?groupId=${groupId}`);
  };

  const handleEditFacility = (facilityId) => {
    // Navigate to facility edit page
    navigate(`/admin/facilities/${facilityId}`);
  };

  // Helper: does a facility match the search?
  const facilityMatchesSearch = (facility) => {
    const term = searchTerm.toLowerCase();
    return (
      facility.name?.toLowerCase().includes(term) ||
      facility.location?.toLowerCase().includes(term)
    );
  };

  // Helper: does a group match the search?
  const groupMatchesSearch = (group, groupFacilities) => {
    const term = searchTerm.toLowerCase();
    return (
      group.name?.toLowerCase().includes(term) ||
      groupFacilities.some(facilityMatchesSearch)
    );
  };

  const hasAccess = userRoles.includes(UserRole.ADMIN) || userRoles.includes(UserRole.FACILITY_ADMIN);
  if (!hasAccess) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          {t('accessDenied')}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const ungroupedFacilities = getFacilitiesForGroup('ungrouped');

  return (
    <Box p={3}>
      <HeaderCard>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0 }}>
          Facility Groups
        </Typography>
{userRoles.includes(UserRole.ADMIN) && (
          <Button
            variant="contained"
            sx={{
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { background: 'rgba(255,255,255,0.25)' },
            }}
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            CREATE GROUP
          </Button>
        )}
      </HeaderCard>

      {/* Search input */}
      <Box mb={2} display="flex" alignItems="center" gap={1}>
        <SearchIcon sx={{ color: 'action.active', fontSize: 22 }} />
        <input
          type="text"
          placeholder="Search by group, facility, address, state, or city"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: 340,
            maxWidth: '100%',
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid #ccc',
            fontSize: 16,
            outline: 'none',
          }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {/* Render Facility Groups */}
            {groups.filter(group => {
              const groupFacilities = getFacilitiesForGroup(group.id);
              // For facility_admin, only show groups that have at least one accessible facility
              if (userRoles.includes(UserRole.FACILITY_ADMIN) && !userRoles.includes(UserRole.ADMIN) && groupFacilities.length === 0) {
                return false;
              }
              return searchTerm.trim() === '' || groupMatchesSearch(group, groupFacilities);
            }).map((group) => {
              const groupFacilities = getFacilitiesForGroup(group.id);
              const filteredFacilities = searchTerm.trim() === '' ? groupFacilities : groupFacilities.filter(facilityMatchesSearch);

              return (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                    <TableCell padding="none">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleGroup(group.id)}
                      >
                        {expandedGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {group.name} ({groupFacilities.length} facilities)
                        </Typography>
                        {userRoles.includes(UserRole.ADMIN) && (
                          <>
                            <IconButton onClick={() => handleOpenDialog(group)} size="small" title="Edit Group" sx={{ ml: 1 }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(group.id)} size="small" title="Delete Group">
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                      {userRoles.includes(UserRole.ADMIN) && (
                        <IconButton 
                          onClick={() => handleAddFacility(group.id)}
                          size="small"
                          color="primary"
                          title="Add Facility"
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Group Facilities */}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={expandedGroups[group.id]} timeout="auto" unmountOnExit>
                        <Table size="small">
                          {filteredFacilities.length > 0 && (
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ pl: 8 }}>
                                  <Box display="flex" alignItems="center">
                                    <Box sx={{ width: 24, mr: 1 }} />
                                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                      Facility Name
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    Location
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    Contact
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    Type
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    Status
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    Actions
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                          )}
                          <TableBody>
                            {filteredFacilities.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ pl: 6 }}>
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    No facilities assigned to this group
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredFacilities.map((facility) => (
                                <TableRow key={facility.id}>
                                  <TableCell sx={{ pl: 8 }}>
                                    <Box display="flex" alignItems="center">
                                      <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                                      <Typography variant="body2">
                                        {facility.name}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {facility.location || 'Not specified'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box>
                                      {facility.contact && (
                                        <Typography variant="body2" color="text.secondary">
                                          {facility.contact}
                                        </Typography>
                                      )}
                                      {facility.phone && (
                                        <Typography variant="body2" color="text.secondary">
                                          📞 {facility.phone}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {facility.type && (
                                      <Chip
                                        label={facility.type}
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={facility.status === 'active' ? 'Enabled' : 'Disabled'}
                                      size="small"
                                      color={facility.status === 'active' ? 'success' : 'error'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleEditFacility(facility.id)}
                                      title="Edit Facility"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {/* Ungrouped Facilities */}
            {ungroupedFacilities.filter(facility => searchTerm.trim() === '' || facilityMatchesSearch(facility)).map((facility) => (
              <React.Fragment key={facility.id}>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableCell padding="none">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleGroup('ungrouped')}
                    >
                      {expandedGroups['ungrouped'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Unassigned Facilities ({ungroupedFacilities.length})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                    <Collapse in={expandedGroups['ungrouped']} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ pl: 8 }}>
                              <Box display="flex" alignItems="center">
                                <Box sx={{ width: 24, mr: 1 }} />
                                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                  Facility Name
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                Location
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                Contact
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                Type
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                Status
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                Actions
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ pl: 8 }}>
                              <Box display="flex" alignItems="center">
                                <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                                <Typography variant="body2">
                                  {facility.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {facility.location || 'Not specified'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {facility.contact && (
                                  <Typography variant="body2" color="text.secondary">
                                    {facility.contact}
                                  </Typography>
                                )}
                                {facility.phone && (
                                  <Typography variant="body2" color="text.secondary">
                                    📞 {facility.phone}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {facility.type && (
                                <Chip
                                  label={facility.type}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={facility.status === 'active' ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={facility.status === 'active' ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditFacility(facility.id)}
                                title="Edit Facility"
                              >
                                <EditIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.address')}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('common.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Fax"
                value={formData.fax}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('common.email')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Locale</InputLabel>
                <Select
                  value={formData.locale}
                  label="Locale"
                  onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                >
                  {localeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Facilities</Typography>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List>
                  {facilities.map(facility => {
                    const isChecked = formData.facilities.includes(facility.id);
                    const assignment = getFacilityAssignment(facility.id);
                    const isDisabled = !!assignment;

                    return (
                      <ListItem
                        key={facility.id}
                        dense
                        button
                        onClick={() => !isDisabled && toggleFacility(facility.id)}
                        disabled={isDisabled}
                      >
                        <Checkbox
                          edge="start"
                          checked={isChecked}
                          tabIndex={-1}
                          disableRipple
                          disabled={isDisabled}
                        />
                        <ListItemText 
                          primary={
                            <Box display="flex" alignItems="center">
                              <BusinessIcon sx={{ mr: 1, fontSize: '1rem' }} />
                              {facility.name}
                            </Box>
                          }
                          secondary={
                            isDisabled 
                              ? `Already assigned to ${assignment.name}` 
                              : facility.location
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FacilityGroups;